import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, OptionKey, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { FetchAPI, FieldAPI, FormAPI, InputAPI, PanelAPI, ToastNotificationAPI, Token } from '@philips-emr/tws-components';
import { Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnChange, OnClick, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import './provider-accreditation-outsourced-services.css';
import './accreditation-token.css';

import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

type FormData = {
  ieAccredited?: string | boolean,
  cdProviderOperator?: string,
  dsOutsourcedService?: string,
  editable?: string,
  ieStatusAccredited?: boolean,
  nrEin?: string,
  nrSeqaccreditation?: number,
  nrSequence?: number,
  outsourcedServiceName?: string,
  workDescription?: string,
};

type MyException = {
  body: string,
};

@Controller({ fragment: 'provider-accreditation-outsourced-services' })
export default class ProviderAccreditationOutsourcedServices extends BaseController {

  @Inject('fetch-outsourced-services')
  private fetchOutsourcedServices: FetchAPI;

  @Inject('fetch-outsourced-service')
  private fetchOutsourcedService: FetchAPI;

  @Inject('form-outsourced-service')
  private formOutsourcedService: FormAPI;

  @Inject('field-provider-code')
  private fieldProviderCode: FieldAPI;

  @Inject('input-provider-code')
  private inputProviderCode: InputAPI;

  @Inject('field-name')
  private fieldName: FieldAPI;

  @Inject('field-employer-identification-number')
  private fieldEmployerIdentificationNumber: FieldAPI;

  @Inject('field-service')
  private fieldService: FieldAPI;

  @Inject('panel-pendencies')
  private panelPendenciesToast: PanelAPI;

  @Inject('toast-notification')
  private toast: ToastNotificationAPI;

  @HPMSOnOptionClick('hpms-custom-card-outsourced-services')
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    const action = {
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSeqUser), Number(nrSeqAccreditation), Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private cleanForm(): void {
    this.formOutsourcedService.setRecord({});
    this.formOutsourcedService.setData({});
    this.showValidations(false);
  }

  private showValidations(validation: boolean): void {
    this.fieldName.setState({ showValidations: validation });
    this.fieldEmployerIdentificationNumber.setState({ showValidations: validation });
    this.fieldProviderCode.setState({ showValidations: validation });
    this.fieldService.setState({ showValidations: validation });
  }

  @OnDynamicContentRender('dynamic-field-accredited')
  public onDynamicContentRenderField(event: RenderEvent): void {
    const { data, render } = event;
    const { ieAccredited } = data as FormData;

    if (ieAccredited === 'S') {
      render((): ReactElement => this.getToken());
    }
  }

  private getToken(): ReactElement {
    return <div className="u-token-main-specialty">
      <Token value="pa.accredited" type={Type.NOTICE} />
    </div>;
  }

  private editRecord(nrSequence: number): void {
    this.fetchOutsourcedService.setParameters({ nrSequence: nrSequence });
    this.fetchOutsourcedService.forceFetch();
  }

  private async deleteRecord(nrSeqUser: number, nrSeqAccreditation: number, nrSequence: number): Promise<void> {
    try {
      await HttpService.delete(Endpoint.DELETE.OUTSOURCED_SERVICES, { nrSeqUser, nrSequence, nrSeqAccreditation });
      this.cleanForm();
      this.fetchOutsourcedServices.forceFetch();
    } catch (e) {
      const { body } = e as MyException;
      ToastServiceProviderAccreditation.error(MessageService.translate(body));
      this.fetchOutsourcedServices.forceFetch();
    }
  }

  @OnClick('button-clean')
  onCleanForm(): void {
    this.cleanForm();
  }

  @OnChange('form-outsourced-service')
  onChangeForm(event: FormAPI): void {
    this.formOutsourcedService.validate();
    const { ieStatusAccredited, cdProviderOperator } = event.value;
    this.inputProviderCode.setEnabled(ieStatusAccredited ? true : false);
    if (ieStatusAccredited) {
      this.fieldProviderCode.setValidators([{ name: 'required', options: {} }] as Validator[]);
      this.fieldProviderCode.validate();
    } else {
      if (cdProviderOperator != 0) {
        this.inputProviderCode.setValue('');
      }
      this.fieldProviderCode.setValidators([{}] as Validator[]);
      this.fieldProviderCode.validate();
    }
  }

  @OnAfterFetch('fetch-outsourced-service')
  onAfterFetchOutsourcedService(event: FetchEvent): void {
    const { ieStatusAccredited } = event.data as FetchAPI;
    const isValid = ieStatusAccredited ? true : false;
    this.inputProviderCode.setEnabled(isValid);
    this.fieldProviderCode.setState({ showValidations: isValid });
    this.showValidations(false);
  }

  @OnClick('button-confirm')
  onclick(): void {
    if (this.formOutsourcedService.validate()) {
      const record = this.formOutsourcedService.getRecord() as FormData;
      const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      this.formOutsourcedService.setState({ loading: true });
      HttpService.post(Endpoint.POST.OUTSOURCED_SERVICES, { nrSeqUser, nrSeqAccreditation }, { body: { ...record } }).
        then(() => {
          this.cleanForm();
          this.formOutsourcedService.setLoading(false);
          this.fetchOutsourcedServices.forceFetch();
        }).catch((response) => {
          ToastServiceProviderAccreditation.error(response);
        });
    } else {
      this.formOutsourcedService.validate();
      this.showValidations(true);
    }
  }

  @OnMount('panel-outsourced-service')
  OnMountPanel(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.OUTSOURCED_SERVICES);
  }

  @HPMSOnCreateMenu('hpms-custom-card-outsourced-services')
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { shouldRender, data } = event;
    const { editable } = data;
    if (editable == 'N') {
      shouldRender(false);
    }
  }

  @OnMount('fetch-pendencies')
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.OUTSOURCED_SERVICES);
  }

  @OnAfterFetch('fetch-pendencies')
  onAfterFetch(toastEvent: FetchEvent): void {
    if (isEmptyArrayOrObject(toastEvent.data)) {
      this.panelPendenciesToast.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, toastEvent);
    }
  }

}
